import { userApis } from '@/axiosModel';
import type { ApiResponse } from '@/axiosModel/Model.class';

export interface UpdateUserSettingsPayload {
  uName?: string;
  phone?: string;
  website?: string;
  company?: string;
  address?: string;
  about?: string;
  fullName?: string;
  personalPhone?: string;
  qqCode?: string;
  postalcode?: string;
  caption?: string;
  invoiceTitle?: string;
  city?: string;
  state?: string;
  countryId?: number;
}

/** get-user 接口返回的用户信息 */
export interface UserInfo extends UpdateUserSettingsPayload {
  userId?: number;
  account?: string;
  uType?: number;
  userImg?: string;
  newTime?: number | string;
  expireTime?: number | string;
  mobilePhone?: string;
  uEmail?: string;
  areaCode?: string;
  hasPassword?: number;
  isUpdateName?: number;
  auditStatus?: number | string;
  isActivate?: number | string;
  isBuy?: number;
  linkName?: string;
  type?: number;
  bannerLogo?: string;
  bannerImg?: string;
  homepage_url?: string;
  panorama_vip_type?: number;
  panorama_vip_expire_time?: number;
  micropage_vip_type?: number;
  micropage_vip_expire_time?: number;
  /** AI 绘本会员等级：0 免费用户，1 基础会员，2 高级会员 */
  aipicturebook_vip_type?: number;
  exhverse_vip_expire_time?: number;
  priceType?: number;
  trial_status?: number;
  buy_year?: number;
  domain?: string;
  last_expire_time?: number;
  last_uType?: number;
  currency?: string;
  favicon?: string;
  add_wechat?: number;
  outputs?: number;
  PrimaryAccountId?: number;
  is_upLoad_book?: number;
  is_upLoad_micropage?: number;
  is_upLoad_panorama?: number;
  verifyPhone?: number;
  homepage_sys_status?: number;
}

/** AI 绘本会员等级名称映射：0 免费用户，1 基础会员，2 高级会员 */
export const USER_LEVEL_NAME_MAP: Record<number, string> = {
  0: '免费用户',
  1: '基础会员',
  2: '高级会员'
};

/** 功能所需会员门槛对应的等级数值 */
export const MEMBERSHIP_REQUIREMENT_LEVEL = {
  basic: 1,
  premium: 2
} as const;

export type MembershipRequirement = keyof typeof MEMBERSHIP_REQUIREMENT_LEVEL;

/**
 * 解析 AI 绘本会员等级（优先 aipicturebook_vip_type，回退 uType）。
 */
export function resolveUserType(
  user: Pick<UserInfo, 'uType' | 'aipicturebook_vip_type'> | null | undefined
): number {
  return Number(user?.aipicturebook_vip_type ?? user?.uType ?? 0);
}

/**
 * 当前用户是否满足指定会员门槛（无门槛视为可用）。
 */
export function hasMembershipAccess(
  user: Pick<UserInfo, 'uType' | 'aipicturebook_vip_type'> | null | undefined,
  required?: MembershipRequirement | null
): boolean {
  if (!required) return true;
  return resolveUserType(user) >= MEMBERSHIP_REQUIREMENT_LEVEL[required];
}

/**
 * 是否为免费用户。
 */
export function isFreeUser(
  user: Pick<UserInfo, 'uType' | 'aipicturebook_vip_type'> | null | undefined
): boolean {
  return resolveUserType(user) === 0;
}

/**
 * 是否为高级会员（AI 绘本会员等级为 2）。
 */
export function isPremiumUser(
  user: Pick<UserInfo, 'uType' | 'aipicturebook_vip_type'> | null | undefined
): boolean {
  return resolveUserType(user) === 2;
}

/**
 * 是否已经绑定手机号。verifyPhone 为主，mobilePhone 用于兼容未返回状态字段的接口。
 */
export function isPhoneBound(
  user: Pick<UserInfo, 'verifyPhone' | 'mobilePhone'> | null | undefined
): boolean {
  if (user?.verifyPhone !== undefined) return Number(user.verifyPhone) === 1;
  return /^1[3-9]\d{9}$/.test(user?.mobilePhone?.trim() ?? '');
}

/**
 * 获取会员等级展示名称。
 */
export function getUserLevelName(
  user: Pick<UserInfo, 'uType' | 'aipicturebook_vip_type'> | null | undefined
): string {
  return USER_LEVEL_NAME_MAP[resolveUserType(user)] || '免费用户';
}

/**
 * 获取用户展示昵称（昵称为空时依次回退账号、linkName）。
 */
export function getUserDisplayName(
  user: UserInfo | null | undefined,
  fallback = 'AI 绘本用户'
): string {
  const candidates = [user?.uName, user?.fullName, user?.linkName, user?.account];

  for (const item of candidates) {
    const text = item?.trim();
    if (text) return text;
  }

  return fallback;
}

/**
 * 格式化接口时间戳为日期字符串。
 */
export function formatUserTime(value?: number | string): string {
  const timestamp = Number(value);
  if (!Number.isFinite(timestamp) || timestamp <= 0) return '';

  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * 获取绘本会员到期时间展示文案。
 */
export function getExhverseExpireText(user: UserInfo | null | undefined): string {
  const exhverseExpire = formatUserTime(user?.exhverse_vip_expire_time);
  if (exhverseExpire) return exhverseExpire;

  return formatUserTime(user?.expireTime);
}

/**
 * 规范化头像地址（避免重复拼接 OSS 裁剪参数）。
 */
export function normalizeUserAvatarUrl(userImg?: string): string {
  const url = userImg?.trim();
  if (!url) return '';
  if (url.includes('x-oss-process=image/resize')) return url;

  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}x-oss-process=image/resize,l_100`;
}

/**
 * 脱敏账号展示（手机号 / 邮箱 / 其他账号）。
 */
export function maskUserAccount(value: string): string {
  const text = value.trim();
  if (!text) return '--';

  if (/^1\d{10}$/.test(text)) {
    return `${text.slice(0, 3)}****${text.slice(-4)}`;
  }

  if (text.includes('@')) {
    const [name = '', domain = ''] = text.split('@');
    const maskedName = name.length <= 2 ? `${name[0] || ''}*` : `${name.slice(0, 2)}***`;
    return `${maskedName}@${domain}`;
  }

  if (text.length <= 4) return text;
  return `${text.slice(0, 2)}****${text.slice(-2)}`;
}

/**
 * 获取当前登录用户信息。
 */
export async function fetchUserInfo(): Promise<UserInfo> {
  const res = (await userApis.getUser().req) as ApiResponse<UserInfo>;
  return res.data ?? {};
}

/**
 * 更新用户设置信息。
 */
export async function updateUserSettings(payload: UpdateUserSettingsPayload): Promise<void> {
  await userApis.updateUserInfo({
    uName: payload.uName ?? '',
    phone: payload.phone,
    company: payload.company,
    about: payload.about,
    website: payload.website,
    address: payload.address
  }).req;
}

/**
 * 退出登录。
 */
export async function signOut(): Promise<void> {
  await userApis.signOut().req;
}
